import { useEffect, useState, type FormEvent } from "react";
import { formatDateTime, formatTime, utcToAucklandInput } from "../../../shared/time";
import type { PlanDetail, ServiceType } from "../../../shared/types";
import { ErrorNote } from "../../ui";
import { addRehearsal, listServiceTypes, updatePlan } from "./client";
import { localInputToUtc, messageOf } from "./model";

export function NotesPanel({ plan, canEdit, onPlan }: {
  plan: PlanDetail;
  canEdit: boolean;
  onPlan: (plan: PlanDetail) => void;
}) {
  const [types, setTypes] = useState<ServiceType[]>([]);
  const [title, setTitle] = useState(plan.title);
  const [serviceTypeId, setServiceTypeId] = useState(plan.serviceTypeId);
  const [when, setWhen] = useState(utcToAucklandInput(plan.startsAt));
  const [status, setStatus] = useState<"draft" | "confirmed">(plan.status === "confirmed" ? "confirmed" : "draft");
  const [series, setSeries] = useState(plan.series ?? "");
  const [notes, setNotes] = useState(plan.notes);
  const [publicNotes, setPublicNotes] = useState(plan.publicNotes);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState("");
  const [busy, setBusy] = useState(false);
  const signature = JSON.stringify({
    id: plan.id,
    title: plan.title,
    serviceTypeId: plan.serviceTypeId,
    startsAt: plan.startsAt,
    status: plan.status,
    series: plan.series,
    notes: plan.notes,
    publicNotes: plan.publicNotes,
  });

  useEffect(() => {
    setTitle(plan.title);
    setServiceTypeId(plan.serviceTypeId);
    setWhen(utcToAucklandInput(plan.startsAt));
    setStatus(plan.status === "confirmed" ? "confirmed" : "draft");
    setSeries(plan.series ?? "");
    setNotes(plan.notes);
    setPublicNotes(plan.publicNotes);
  }, [signature]);

  useEffect(() => {
    let cancel = false;
    void listServiceTypes().then((rows) => { if (!cancel) setTypes(rows); }).catch(() => { if (!cancel) setTypes([]); });
    return () => { cancel = true; };
  }, []);

  async function save(event: FormEvent) {
    event.preventDefault();
    const startsAt = localInputToUtc(when);
    if (!title.trim()) { setError("Give the plan a title."); return; }
    if (!startsAt) { setError("Enter a valid Auckland date and time."); return; }
    setBusy(true);
    setError("");
    setSaved("");
    try {
      onPlan(await updatePlan(plan.id, {
        title: title.trim(),
        serviceTypeId,
        startsAt,
        status,
        notes,
        publicNotes,
        series: series.trim() ? series.trim() : null,
      }));
      setSaved("Plan details saved.");
    } catch (caught) {
      setError(messageOf(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack">
      <div>
        <h2>Notes and rehearsals</h2>
        <p className="muted">Team notes stay with the planners. Public notes are the words that can be shown outside the team.</p>
      </div>
      {error && <ErrorNote>{error}</ErrorNote>}
      {saved && <p className="muted" role="status">{saved}</p>}
      {canEdit ? (
        <form className="panel panel-pad form-grid" onSubmit={(event) => void save(event)}>
          <div className="svc-editor-grid">
            <div className="field"><label htmlFor="edit-title">Title</label><input id="edit-title" value={title} maxLength={200} onChange={(event) => setTitle(event.target.value)} /></div>
            <div className="field"><label htmlFor="edit-when">Starts (Auckland)</label><input id="edit-when" type="datetime-local" value={when} onChange={(event) => setWhen(event.target.value)} /></div>
            <div className="field">
              <label htmlFor="edit-type">Service type</label>
              <select id="edit-type" value={serviceTypeId} onChange={(event) => setServiceTypeId(event.target.value)}>
                {types.map((type) => <option key={type.id} value={type.id}>{type.name}</option>)}
                {!types.some((type) => type.id === serviceTypeId) && <option value={serviceTypeId}>{plan.serviceTypeName}</option>}
              </select>
            </div>
            <div className="field">
              <label htmlFor="edit-status">Status</label>
              <select id="edit-status" value={status} onChange={(event) => setStatus(event.target.value === "confirmed" ? "confirmed" : "draft")}>
                <option value="draft">Draft</option>
                <option value="confirmed">Confirmed</option>
              </select>
            </div>
          </div>
          {serviceTypeId !== plan.serviceTypeId && <p className="notice">Changing the service type does not rebuild teams or positions.</p>}
          <p className="faint">Moving the start time shifts every row on the order.</p>
          <div className="field"><label htmlFor="edit-series">Series</label><input id="edit-series" value={series} maxLength={160} onChange={(event) => setSeries(event.target.value)} /></div>
          <div className="field"><label htmlFor="edit-notes">Team notes</label><textarea id="edit-notes" maxLength={8000} value={notes} onChange={(event) => setNotes(event.target.value)} /></div>
          <div className="field"><label htmlFor="edit-public">Public notes</label><textarea id="edit-public" maxLength={8000} value={publicNotes} onChange={(event) => setPublicNotes(event.target.value)} /></div>
          <button className="btn primary" type="submit" disabled={busy}>{busy ? "Saving…" : "Save details"}</button>
        </form>
      ) : (
        <article className="panel panel-pad stack">
          <h3>Team notes</h3>
          <p className="svc-prose">{plan.notes || "No team notes."}</p>
          <h3>Public notes</h3>
          <p className="svc-prose">{plan.publicNotes || "No public notes."}</p>
          {plan.series && <p className="muted">Series · {plan.series}</p>}
        </article>
      )}
      <RehearsalList plan={plan} canEdit={canEdit} onPlan={onPlan} />
    </div>
  );
}

function RehearsalList({ plan, canEdit, onPlan }: {
  plan: PlanDetail;
  canEdit: boolean;
  onPlan: (plan: PlanDetail) => void;
}) {
  const [name, setName] = useState("Rehearsal");
  const [start, setStart] = useState(() => utcToAucklandInput(new Date(new Date(plan.startsAt).getTime() - 90 * 60 * 1000).toISOString()));
  const [end, setEnd] = useState(() => utcToAucklandInput(plan.startsAt));
  const [place, setPlace] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const startsAt = localInputToUtc(start);
    const endsAt = end.trim() ? localInputToUtc(end) : null;
    if (!name.trim()) { setError("Name the rehearsal."); return; }
    if (!startsAt || (end.trim() && !endsAt)) { setError("Enter a valid rehearsal time."); return; }
    if (endsAt && endsAt <= startsAt) { setError("The rehearsal should end after it starts."); return; }
    setBusy(true);
    setError("");
    try {
      onPlan(await addRehearsal(plan.id, { name: name.trim(), startsAt, endsAt, place: place.trim(), notes }));
      setName("Rehearsal");
      setPlace("");
      setNotes("");
    } catch (caught) {
      setError(messageOf(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className="panel panel-pad stack">
      <h3>Rehearsals</h3>
      {canEdit && <p className="faint">A rehearsal can be added. Editing or removing one after it is saved is not available.</p>}
      {plan.rehearsals.length === 0 && <p className="muted">No rehearsal time yet.</p>}
      {plan.rehearsals.map((rehearsal) => (
        <div className="svc-rehearsal" key={rehearsal.id}>
          <strong>{rehearsal.name}</strong>
          <span className="muted">{formatDateTime(rehearsal.startsAt)}{rehearsal.endsAt ? ` – ${formatTime(rehearsal.endsAt)}` : ""}</span>
          {rehearsal.place && <span>{rehearsal.place}</span>}
          {rehearsal.notes && <p className="muted svc-prose">{rehearsal.notes}</p>}
        </div>
      ))}
      {error && <ErrorNote>{error}</ErrorNote>}
      {canEdit && (
        <form className="form-grid" onSubmit={(event) => void submit(event)}>
          <div className="field"><label htmlFor="reh-name">Name</label><input id="reh-name" value={name} maxLength={160} onChange={(event) => setName(event.target.value)} /></div>
          <div className="svc-editor-grid">
            <div className="field"><label htmlFor="reh-start">Starts</label><input id="reh-start" type="datetime-local" value={start} onChange={(event) => setStart(event.target.value)} /></div>
            <div className="field"><label htmlFor="reh-end">Ends</label><input id="reh-end" type="datetime-local" value={end} onChange={(event) => setEnd(event.target.value)} /></div>
          </div>
          <div className="field"><label htmlFor="reh-place">Place</label><input id="reh-place" value={place} maxLength={200} onChange={(event) => setPlace(event.target.value)} /></div>
          <div className="field"><label htmlFor="reh-notes">Notes</label><textarea id="reh-notes" maxLength={2000} value={notes} onChange={(event) => setNotes(event.target.value)} /></div>
          <button className="btn" type="submit" disabled={busy}>{busy ? "Adding…" : "Add rehearsal"}</button>
        </form>
      )}
    </article>
  );
}
